import classNames from 'classnames';
import { CheckIcon, Download, FileText, User } from 'lucide-react';
import { memo, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  CalendarEventData,
  CheckoutCartData,
  COMPONENT_TYPE_CALENDAR_EVENT,
  COMPONENT_TYPE_CHECKOUT_CART,
  COMPONENT_TYPE_CTA_BUTTON,
  COMPONENT_TYPE_PRODUCT_CAROUSEL,
  CtaButtonData,
  Message,
  MessageAttachment,
  ProductCarouselData,
} from '@/Domain/api/Conversation/dto/Message.dto';
import { AIActionsCollapsible } from '@/Domain/components/Inbox/Message/AIActionsCollapsible';
import { AudioPlayer } from '@/Domain/components/Inbox/Message/AudioPlayer';
import { DeliveryStatusIndicator } from '@/Domain/components/Inbox/Message/DeliveryStatusIndicator';
import { InboxCheckoutCart } from '@/Domain/components/Inbox/Message/InboxCheckoutCart';
import { InboxCtaCard } from '@/Domain/components/Inbox/Message/InboxCtaCard';
import { InboxMeetingBooked } from '@/Domain/components/Inbox/Message/InboxMeetingBooked';
import { InboxProductCarousel } from '@/Domain/components/Inbox/Message/InboxProductCarousel';
import { MessageFeedbackButton } from '@/Domain/components/Inbox/Message/MessageFeedbackButton';
import classes from '@/Domain/components/Inbox/Message/MessageItem.module.scss';
import { WhyAISaidThatButton } from '@/Domain/components/Inbox/Message/WhyAISaidThatButton';
import { WhyAISaidThatPopover } from '@/Domain/components/Inbox/Message/WhyAISaidThatPopover';
import { Avatar, AvatarFallback, AvatarImage } from '@/Domain/components/Shared/Avatar/Avatar';
import { IconButton } from '@/Domain/components/Shared/Button/Button';
import {
  Tooltip,
  TooltipTrigger,
  TooltipContent,
} from '@/Domain/components/Shared/Tooltip/Tooltip';
import { CopyIcon, CopyIconHandle } from '@/Domain/components/icons/CopyIcon';
import { MessageAuthorEnum } from '@/Domain/enum/MessageAuthor.enum';
import { UserHelper } from '@/Domain/helper/User.helper';
import { sanitizeVisitorHtml, sanitizeWithExternalLinks } from '@/Domain/helper/sanitize.helper';
import { useAiAssistant } from '@/Domain/hooks/AI/useAiAssistant.hook';
import { copyToClipboard } from '@/Domain/utils/copyToClipboard';
import { safeFormat } from '@/Domain/utils/safeDate';

const AttachmentImage = ({ attachment }: { attachment: MessageAttachment }) => (
  <a href={attachment.url} target="_blank" rel="noopener noreferrer">
    <img
      src={attachment.url}
      alt={attachment.filename || 'Image'}
      className="max-w-[280px] rounded-lg cursor-pointer hover:opacity-90 transition-opacity"
      loading="lazy"
      data-testid="attachment-image"
    />
  </a>
);

const AttachmentFile = ({ attachment }: { attachment: MessageAttachment }) => (
  <a
    href={attachment.url}
    target="_blank"
    rel="noopener noreferrer"
    className="flex items-center gap-2 bg-muted border border-border rounded-lg p-2 hover:bg-accent transition-colors max-w-[280px]"
    data-testid="attachment-file"
  >
    <div className="h-8 w-8 rounded bg-accent flex items-center justify-center shrink-0">
      <FileText className="h-4 w-4 text-foreground-subtle" />
    </div>
    <span className="text-sm text-muted-foreground truncate min-w-0 flex-1">
      {attachment.filename || 'File'}
    </span>
    <Download className="h-3.5 w-3.5 text-foreground-subtle shrink-0" />
  </a>
);

const AttachmentAudio = ({ attachment }: { attachment: MessageAttachment }) => (
  <AudioPlayer
    src={attachment.url}
    mimeType={attachment.mime_type || 'audio/ogg'}
    durationSeconds={attachment.duration_seconds}
  />
);

type AttachmentGroup =
  | { kind: 'cta'; items: CtaButtonData[] }
  | { kind: 'single'; attachment: MessageAttachment };

// One card around each consecutive CTA run keeps its lead-in from reading as reply text.
const groupAttachments = (attachments: MessageAttachment[]): AttachmentGroup[] =>
  attachments.reduce<AttachmentGroup[]>((groups, attachment) => {
    if (attachment.attachment_type !== COMPONENT_TYPE_CTA_BUTTON || !attachment.data) {
      return [...groups, { kind: 'single', attachment }];
    }
    const previous = groups[groups.length - 1];
    if (previous?.kind === 'cta') {
      return [
        ...groups.slice(0, -1),
        { ...previous, items: [...previous.items, attachment.data as CtaButtonData] },
      ];
    }
    return [...groups, { kind: 'cta', items: [attachment.data as CtaButtonData] }];
  }, []);

const MessageAttachments = ({ attachments }: { attachments: MessageAttachment[] }) => {
  if (!attachments || attachments.length === 0) return null;

  return (
    <div className="flex flex-col gap-2 mt-1">
      {groupAttachments(attachments).map((group, index) => {
        if (group.kind === 'cta') {
          return <InboxCtaCard key={index} items={group.items} />;
        }
        const attachment = group.attachment;
        if (attachment.attachment_type === COMPONENT_TYPE_PRODUCT_CAROUSEL && attachment.data) {
          return <InboxProductCarousel key={index} data={attachment.data as ProductCarouselData} />;
        }
        if (attachment.attachment_type === COMPONENT_TYPE_CHECKOUT_CART && attachment.data) {
          return <InboxCheckoutCart key={index} data={attachment.data as CheckoutCartData} />;
        }
        if (attachment.attachment_type === COMPONENT_TYPE_CALENDAR_EVENT && attachment.data) {
          return <InboxMeetingBooked key={index} data={attachment.data as CalendarEventData} />;
        }
        if (attachment.attachment_type === 'image') {
          return <AttachmentImage key={index} attachment={attachment} />;
        }
        if (attachment.attachment_type === 'audio') {
          return <AttachmentAudio key={index} attachment={attachment} />;
        }
        // Component types carry no url, so a catch-all file chip would render an empty download
        // link for any type this bundle predates.
        if (attachment.data) {
          return null;
        }
        return <AttachmentFile key={index} attachment={attachment} />;
      })}
    </div>
  );
};

type Props = {
  message: Message;
  // Not the DTO: its identity churns on every conversation_updated frame, which would defeat the memo.
  conversationId: string | undefined;
  visitorLabel: string | null | undefined;
  previousMessage: Message | null;
};

const MessageItemRow = ({ message, conversationId, visitorLabel, previousMessage }: Props) => {
  const { t } = useTranslation(['conversation', 'inbox_v2']);

  const { aiAssistant, isLoading } = useAiAssistant();
  const [copiedMessage, setCopiedMessage] = useState(false);
  const [whyAISaidThatOpen, setWhyAISaidThatOpen] = useState(false);
  const copyIconRef = useRef<CopyIconHandle>(null);

  const getPlainTextFromHtml = (html: string): string => {
    const tmp = document.createElement('div');
    tmp.innerHTML = html;
    // <img> has no textContent, so surface its URL rather than dropping it from the copied plain text.
    tmp.querySelectorAll('img').forEach((img) => {
      const src = img.getAttribute('src');
      if (src) img.replaceWith(document.createTextNode(src));
    });
    // textContent collapses <br> and block boundaries, so restore the line breaks an operator expects when copying.
    tmp.querySelectorAll('br').forEach((br) => br.replaceWith(document.createTextNode('\n')));
    tmp.querySelectorAll('p, div, li, tr, h1, h2, h3, h4, h5, h6').forEach((el) => {
      el.appendChild(document.createTextNode('\n'));
    });
    return (tmp.textContent || tmp.innerText || '').replace(/\n{3,}/g, '\n\n').trim();
  };

  const handleCopyMessage = (htmlMessage: string) => {
    const plainText = getPlainTextFromHtml(htmlMessage);
    copyToClipboard(
      plainText,
      () => {
        setCopiedMessage(true);
        setTimeout(() => setCopiedMessage(false), 1500);
      },
      undefined,
      htmlMessage,
    );
  };

  const messageCreatedAt = (createdDate: string) => safeFormat(createdDate, 'h:mm a');

  const isInMessage = message.author.type === MessageAuthorEnum.VISITOR;
  const isOutMessage =
    message.author.type === MessageAuthorEnum.AI ||
    message.author.type === MessageAuthorEnum.HUMAN_OPERATOR;

  const messageHtml = useMemo(() => {
    const raw = message.content_translated || message.content_inbox || '';
    // Visitor text (incl. its translation, which has no author guard) must not become a clickable link or inline image.
    return isInMessage ? sanitizeVisitorHtml(raw) : sanitizeWithExternalLinks(raw);
  }, [message.content_translated, message.content_inbox, isInMessage]);

  const visitorName = useMemo(() => visitorLabel || t('inbox_v2:un_known_visitor'), [visitorLabel]);

  const messageLabel = useMemo(() => {
    if (message.author.type === MessageAuthorEnum.HUMAN_OPERATOR) {
      return UserHelper.getFullName(message.author.user) || message.author.user.email;
    }

    if (message.author.type === MessageAuthorEnum.AI) {
      return aiAssistant?.current_ai_assistant.name;
    }

    return visitorName;
  }, [aiAssistant, message.id, message.author.type, isLoading, visitorName]);

  const messageAvatar = useMemo(() => {
    if (message.author.type === MessageAuthorEnum.HUMAN_OPERATOR) {
      return message.author.user.avatar_url || undefined;
    }

    return aiAssistant?.current_ai_assistant.avatar_image;
  }, [aiAssistant, message.id, message.author.type, isLoading, visitorName]);

  const isDifferentAuthor = useMemo(() => {
    if (!previousMessage) return true;
    if (!message) return true;
    if (previousMessage.author?.type !== message.author.type) return true;

    if (
      message.author.type === MessageAuthorEnum.HUMAN_OPERATOR &&
      previousMessage.author?.type === MessageAuthorEnum.HUMAN_OPERATOR
    ) {
      return message.author.user.email !== previousMessage.author.user.email;
    }

    return false;
  }, [message, previousMessage]);

  if (!message?.author) {
    return null;
  }

  return (
    <li key={message.id} className="flex w-full flex-col gap-1.5">
      <div
        className={classNames('flex max-w-full xl:max-w-[80%]', {
          'self-start flex-row-reverse': isInMessage,
          'self-end': isOutMessage,
        })}
      >
        <div
          className={classNames('flex flex-col min-w-0', {
            'items-end': isOutMessage,
            'items-start': isInMessage,
          })}
        >
          {isDifferentAuthor && (
            <div
              className={classNames('flex items-center gap-2 mb-1.5', {
                'flex-row-reverse': isInMessage,
              })}
            >
              <span className="text-sm text-foreground-subtle font-medium whitespace-nowrap">
                {messageLabel}
              </span>
              <Avatar className="h-5 w-5">
                <AvatarImage src={isInMessage ? '' : messageAvatar} />
                <AvatarFallback className="bg-accent">
                  <User className="text-foreground-subtle size-3" />
                </AvatarFallback>
              </Avatar>
            </div>
          )}

          <div className="flex flex-col-reverse justify-end gap-0.5 max-w-full">
            <div
              className={classNames('flex flex-row justify-between items-center gap-1', {
                'flex-row-reverse': isOutMessage,
              })}
            >
              <div className="flex items-center gap-0.5">
                <time className={classNames('text-xs text-foreground-subtle self-center px-1.5')}>
                  {messageCreatedAt(message.created_at)}
                </time>
                {isOutMessage && message.delivery_status && (
                  <DeliveryStatusIndicator
                    status={message.delivery_status}
                    errorMessage={message.delivery_error}
                  />
                )}
              </div>
              <div className="flex flex-row items-center gap-0 pl-1">
                {isOutMessage && (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <IconButton
                        variant="ghost"
                        size="xs"
                        data-testid="copy-message-button"
                        onClick={() => handleCopyMessage(messageHtml)}
                        onMouseEnter={() => copyIconRef.current?.startAnimation()}
                        onMouseLeave={() => copyIconRef.current?.stopAnimation()}
                        className={copiedMessage ? 'pointer-events-none' : ''}
                      >
                        {copiedMessage ? <CheckIcon /> : <CopyIcon ref={copyIconRef} size={14} />}
                      </IconButton>
                    </TooltipTrigger>
                    <TooltipContent side="bottom">
                      {copiedMessage ? t('inbox_v2:tooltip.copied') : t('inbox_v2:tooltip.copy')}
                    </TooltipContent>
                  </Tooltip>
                )}
                {isOutMessage &&
                  message.author.type === MessageAuthorEnum.AI &&
                  !message.is_welcome &&
                  conversationId && (
                    <>
                      <MessageFeedbackButton
                        conversationId={conversationId}
                        messageId={message.id}
                        existingFeedback={message.feedback}
                      />
                      <WhyAISaidThatButton
                        messageId={message.id}
                        onClick={() => setWhyAISaidThatOpen(true)}
                      />
                    </>
                  )}
              </div>
            </div>

            {isOutMessage &&
              message.author.type === MessageAuthorEnum.AI &&
              !message.is_welcome &&
              message.tool_calls &&
              message.tool_calls.length > 0 && (
                <AIActionsCollapsible toolCalls={message.tool_calls} />
              )}

            <div
              className={`group text-sm flex flex-col relative ${
                isOutMessage ? 'items-end' : 'items-start'
              }`}
            >
              <div className="inline-flex flex-col max-w-full">
                {message.attachments && message.attachments.length > 0 && (
                  <MessageAttachments
                    attachments={message.attachments.filter(
                      (a) =>
                        a.attachment_type === COMPONENT_TYPE_PRODUCT_CAROUSEL ||
                        a.attachment_type === COMPONENT_TYPE_CHECKOUT_CART ||
                        a.attachment_type === COMPONENT_TYPE_CALENDAR_EVENT,
                    )}
                  />
                )}
                {isOutMessage &&
                message.author.type === MessageAuthorEnum.AI &&
                !message.is_welcome &&
                conversationId ? (
                  <WhyAISaidThatPopover
                    message={message}
                    conversationId={conversationId}
                    open={whyAISaidThatOpen}
                    onOpenChange={setWhyAISaidThatOpen}
                  >
                    <div
                      key={message.content_translated ? message.content_translated : undefined}
                      className={classNames(
                        `p-3.5 rounded-xl text-foreground break-words break-text ${classes.messageItemHtml} animate-scaleUp prose prose-sm cursor-pointer`,
                        {
                          'bg-primary-muted hover:bg-primary-muted/80': isOutMessage,
                        },
                      )}
                      dangerouslySetInnerHTML={{ __html: messageHtml }}
                      data-testid={`why-ai-said-that-message-${message.id}`}
                    />
                  </WhyAISaidThatPopover>
                ) : (
                  (message.content_translated || message.content_inbox) && (
                    <div
                      key={message.content_translated ? message.content_translated : undefined}
                      className={classNames(
                        `p-3.5 rounded-xl text-foreground break-words break-text ${classes.messageItemHtml} animate-scaleUp prose prose-sm`,
                        {
                          'bg-muted': isInMessage,
                          'bg-primary-muted': isOutMessage,
                        },
                      )}
                      dangerouslySetInnerHTML={{ __html: messageHtml }}
                    />
                  )
                )}
                {message.attachments && message.attachments.length > 0 && (
                  <MessageAttachments
                    attachments={message.attachments.filter(
                      (a) =>
                        a.attachment_type !== COMPONENT_TYPE_PRODUCT_CAROUSEL &&
                        a.attachment_type !== COMPONENT_TYPE_CHECKOUT_CART &&
                        a.attachment_type !== COMPONENT_TYPE_CALENDAR_EVENT,
                    )}
                  />
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </li>
  );
};

export const MessageItem = memo(MessageItemRow);

MessageItem.displayName = 'MessageItem';
